import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["image", "spinner", "container", "errorState"]
  static values = {
    timeout: { type: Number, default: 10000 }, // 10 secondes de timeout
    retryCount: { type: Number, default: 2 },
  }

  connect() {
    this.currentRetries = 0
    this.isLoading = false
    this.isLoaded = false
    this.hasError = false

    // Initialiser l'état
    this.initializeState()
    this.loadImage()
  }

  initializeState() {
    // S'assurer qu'un seul état est visible au départ
    this.showSpinner()
    this.hideError()
    this.hideImage()
  }

  loadImage() {
    if (!this.hasImageTarget) {
      this.showError("Aucune image à charger")
      return
    }

    const img = this.imageTarget
    const originalSrc = img.dataset.src || img.src

    if (!originalSrc) {
      this.showError("Aucune source d'image")
      return
    }

    // Réinitialiser les états
    this.isLoading = true
    this.isLoaded = false
    this.hasError = false

    // Afficher uniquement le spinner
    this.showSpinner()
    this.hideError()
    this.hideImage()


    // Créer une nouvelle image pour le préchargement
    const preloadImg = new Image()

    // Timeout pour éviter les chargements trop longs
    const timeoutId = setTimeout(() => {
      if (this.isLoading) {
        this.handleLoadError("Timeout de chargement")
      }
    }, this.timeoutValue)

    // Gestionnaire de succès
    preloadImg.onload = () => {
      clearTimeout(timeoutId)
      if (this.isLoading) {
        this.handleLoadSuccess(originalSrc)
      }
    }

    // Gestionnaire d'erreur
    preloadImg.onerror = () => {
      clearTimeout(timeoutId)
      if (this.isLoading) {
        this.handleLoadError("Erreur de chargement")
      }
    }

    // Démarrer le chargement
    preloadImg.src = originalSrc
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
      this.spinnerTarget.setAttribute("aria-hidden", "false")
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
      this.spinnerTarget.setAttribute("aria-hidden", "true")
    }
  }

  showImage() {
    if (this.hasImageTarget) {
      this.imageTarget.classList.remove("admin-image-loading")
      this.imageTarget.classList.add("admin-image-loaded", "admin-image-fade-in")
      this.imageTarget.style.display = "block"
    }
  }

  hideImage() {
    if (this.hasImageTarget) {
      this.imageTarget.classList.add("admin-image-loading")
      this.imageTarget.classList.remove("admin-image-loaded", "admin-image-fade-in")
    }
  }

  showError(message) {
    if (this.hasErrorStateTarget) {
      this.errorStateTarget.classList.remove("hidden")
      const errorText = this.errorStateTarget.querySelector(".admin-error-text")
      if (errorText) {
        errorText.textContent = message
      }
    }
  }

  hideError() {
    if (this.hasErrorStateTarget) {
      this.errorStateTarget.classList.add("hidden")
    }
  }

  handleLoadSuccess(src) {

    // Mettre à jour les états
    this.isLoading = false
    this.isLoaded = true
    this.hasError = false

    // Mettre à jour la source de l'image
    if (this.hasImageTarget) {
      this.imageTarget.src = src
    }

    // Afficher uniquement l'image
    this.hideSpinner()
    this.hideError()

    // Petit délai pour l'effet de transition
    setTimeout(() => {
      this.showImage()
    }, 100)

    // Dispatch d'un événement personnalisé
    this.dispatch("loaded", { detail: { src } })
  }

  handleLoadError(errorMessage) {

    // Tentative de rechargement
    if (this.currentRetries < this.retryCountValue) {
      this.currentRetries++

      // Garder le spinner pendant le retry
      setTimeout(() => {
        if (!this.isLoaded) {
          this.loadImage()
        }
      }, 1000 * this.currentRetries) // Délai progressif

      return
    }

    // Mettre à jour les états
    this.isLoading = false
    this.isLoaded = false
    this.hasError = true

    // Afficher uniquement l'erreur
    this.hideSpinner()
    this.hideImage()
    this.showError(errorMessage)

    // Dispatch d'un événement d'erreur
    this.dispatch("error", { detail: { error: errorMessage } })
  }

  // Action pour relancer le chargement manuellement
  retry() {
    this.currentRetries = 0
    this.isLoading = false
    this.isLoaded = false
    this.hasError = false

    this.initializeState()
    this.loadImage()
  }

  // Nettoyage à la déconnexion
  disconnect() {
    this.isLoading = false
  }
}
